export const dentistsData = {
  industry: "Dentists",
  slug: "dentists",
  
  seo: {
    title: "Intake Forms for Dentists | HeyIntake",
    description: "Streamline patient intake for your dental practice with digital forms. Capture medical history, insurance info, and symptoms before appointments to reduce wait times.",
    keywords: [
      "dental intake form",
      "patient registration form dentist",
      "dental medical history form",
      "dentist new patient form",
      "dental insurance intake",
      "dental practice intake"
    ]
  },

  heroTitle: "Intake Forms for Dentists",
  heroSubtitle: "Capture patient history, insurance details, and symptoms before they arrive. Reduce wait times, improve care.",
  heroDescription: "Stop making patients fill out clipboards in the waiting room. HeyIntake lets new patients complete medical history, insurance information, and visit reasons from their phone before their appointment.",
  
  whyAIScoring: {
    title: "Why Dental Practices Need Digital Intake",
    intro: "The waiting room clipboard is outdated. Patients expect the same digital convenience from their dentist that they get from every other service.",
    paragraphs: [
      "Paper intake forms create bottlenecks. New patients arrive and spend 15-20 minutes filling out forms while your schedule backs up. Staff then re-enter the same information into your practice management system — wasting time and introducing errors.",
      "HeyIntake modernizes your intake process. Patients complete comprehensive medical history, current medications, dental concerns, and insurance details on their phone before arriving. The data flows directly into your workflow, and our AI flags potential concerns like medication interactions or complex medical histories.",
      "For general dentists, orthodontists, oral surgeons, and pediatric practices, this means shorter wait times, more accurate records, happier patients, and staff who can focus on care instead of paperwork."
    ]
  },

  painPoints: [
    {
      title: "Waiting Room Bottlenecks",
      description: "New patients spend 15-20 minutes on clipboards while your schedule backs up and other patients wait."
    },
    {
      title: "Illegible Handwriting",
      description: "Staff struggle to read patient forms, leading to errors and follow-up calls for clarification."
    },
    {
      title: "Incomplete Medical History",
      description: "Patients forget medications, allergies, or conditions that could affect treatment safety."
    },
    {
      title: "Insurance Verification Delays",
      description: "Missing or incorrect insurance info discovered at check-in, causing billing headaches."
    },
    {
      title: "Double Data Entry",
      description: "Staff manually re-keying paper forms into your practice management system."
    },
    {
      title: "Outdated Patient Records",
      description: "No easy way for returning patients to update their information between visits."
    }
  ],

  exampleFields: [
    {
      category: "Contact Information",
      fields: [
        "Full legal name",
        "Date of birth",
        "Phone and email",
        "Emergency contact",
        "Preferred contact method"
      ]
    },
    {
      category: "Medical History",
      fields: [
        "Current medications",
        "Known allergies (especially latex, antibiotics)",
        "Medical conditions (diabetes, heart disease, etc.)",
        "Previous surgeries",
        "Pregnancy status (if applicable)"
      ]
    },
    {
      category: "Dental History",
      fields: [
        "Last dental visit date",
        "Previous dentist name",
        "Current dental concerns",
        "History of dental anxiety",
        "Previous dental work (crowns, implants, etc.)"
      ]
    },
    {
      category: "Insurance & Billing",
      fields: [
        "Insurance provider",
        "Policy holder name",
        "Member/Group ID",
        "Insurance phone number",
        "Preferred payment method"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Dental Patient Intake Form",
    description: "Here's what a professional dental intake form looks like:",
    fields: [
      { label: "Reason for today's visit", type: "select", options: ["Routine checkup/cleaning", "Tooth pain", "Cosmetic consultation", "Emergency", "New patient exam"] },
      { label: "When was your last dental visit?", type: "select", options: ["Within 6 months", "6-12 months ago", "1-2 years ago", "2+ years ago"] },
      { label: "Do you have dental insurance?", type: "select", options: ["Yes", "No", "Not sure"] },
      { label: "Any allergies to medications?", type: "textarea" },
      { label: "Current medications (if any)", type: "textarea" },
      { label: "Do you experience dental anxiety?", type: "select", options: ["No", "Mild", "Moderate", "Severe - need sedation options"] },
      { label: "Describe your current dental concern", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Dental Practices",
    subtitle: "From appointment booking to prepared patient visit",
    steps: [
      {
        title: "Patient Completes Intake",
        description: "New patients receive your intake link via text or email and complete forms on their phone."
      },
      {
        title: "AI Reviews & Flags",
        description: "Our AI organizes information and flags potential concerns like drug allergies or complex medical history."
      },
      {
        title: "Patient Arrives Ready",
        description: "Skip the clipboard. Patient checks in, you review their complete history, and treatment begins."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Assisted Patient Intake for Dentists",
    description: "HeyIntake's AI helps organize patient information and flag items requiring clinical attention before the appointment.",
    factors: [
      "Medical history complexity indicators",
      "Potential drug interaction flags",
      "Dental anxiety level assessment",
      "Insurance verification status",
      "Urgency of dental concern"
    ],
    exampleLead: {
      name: "Robert Martinez",
      context: "New Patient • Tooth Pain • Delta Dental",
      summary: "New patient with tooth pain in lower right molar, started 3 days ago. On blood thinners (warfarin). Delta Dental insurance verified. Moderate dental anxiety - may need extra time.",
      score: 88
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Flag Medical Concerns",
      description: "AI highlights patients on blood thinners, with heart conditions, or other factors affecting treatment."
    },
    {
      icon: "clock",
      title: "Eliminate Wait Room Paperwork",
      description: "Patients complete everything before arrival. Check-in takes seconds, not minutes."
    },
    {
      icon: "file",
      title: "Verify Insurance in Advance",
      description: "Collect insurance details early so your team can verify coverage before the appointment."
    },
    {
      icon: "users",
      title: "Customize by Visit Type",
      description: "Different forms for new patients, orthodontic consults, emergency visits, and cosmetic consultations."
    },
    {
      icon: "zap",
      title: "Automate Reminders",
      description: "Send appointment confirmations and pre-visit instructions automatically."
    },
    {
      icon: "chart",
      title: "Track Patient Pipeline",
      description: "See scheduled patients, incomplete forms, and upcoming appointments in one dashboard."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Med Spas", path: "/intake-form-for-med-spas" },
    { name: "Intake Forms for Therapists", path: "/intake-form-for-therapists" },
    { name: "Intake Forms for Fitness Trainers", path: "/intake-form-for-fitness-trainers" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" }
  ],

  ctaText: "Ready to Modernize Your Patient Intake?"
};
